import type { Pool } from '@/api/endpoints/pools'
import { dayLabel, factoryAccent, initials } from '@/features/scheduler/channelCards'

/**
 * Os cards de pool do Scheduler e da tela Pools: estoque, dias de estoque e o
 * aviso de pedido sem vídeo, a partir de ``GET /api/v1/pools``. Puro, para
 * testar sem DOM. Ver docs/plan-video-pools.md §4.
 */

/** ``idle``: sem canal ligado, a geração pausa. */
export type StockLevel = 'ok' | 'low' | 'empty' | 'idle'

export interface PoolMemberModel {
  slug: string
  name: string
  initials: string
  avatarUrl: string | null
}

export interface PoolCardModel {
  slug: string
  name: string
  language: string
  factory: string
  factoryLabel: string
  accent: string
  /** "todo dia às 14:00"; vazio sem horário. */
  schedule: string
  videosPerDay: number
  /** Vídeos publicáveis que a geração rende por dia: "13 vídeos/dia" (no cortes, os cortes). */
  productionText: string
  consumptionPerDay: number
  available: number
  daysOfStock: number | null
  stock: StockLevel
  /** "≈ 14 dias de estoque", "sem estoque", "gera o que os canais pedem". */
  stockText: string
  /** 0–100, para a barra. */
  stockPercent: number
  members: PoolMemberModel[]
  /** O último pedido sem vídeo das últimas 24 h, escrito; ``null`` sem nenhum. */
  alert: string | null
  enabled: boolean
}

/** Abaixo disto, o estoque fica amarelo. */
export const LOW_STOCK_DAYS = 2
/** A barra enche em duas semanas de estoque. */
const FULL_BAR_DAYS = 14

const LANGUAGE_LABEL: Readonly<Record<string, string>> = { 'pt-br': 'PT-BR', en: 'EN' }

export function languageLabel(language: string): string {
  return LANGUAGE_LABEL[language] ?? language.toUpperCase()
}

function stockOf(pool: Pool): { level: StockLevel; text: string; percent: number } {
  if (pool.members.length === 0) {
    return { level: 'idle', text: 'sem canais — geração pausada', percent: 0 }
  }
  if (pool.available === 0) return { level: 'empty', text: 'sem estoque', percent: 0 }
  const days = pool.days_of_stock ?? null
  if (days === null) {
    return { level: 'ok', text: 'gera o que os canais pedem', percent: 100 }
  }
  const text = `≈ ${days.toLocaleString('pt-BR', { maximumFractionDigits: 1 })} ${days === 1 ? 'dia' : 'dias'} de estoque`
  return {
    level: days < LOW_STOCK_DAYS ? 'low' : 'ok',
    text,
    percent: Math.max(4, Math.min(100, Math.round((days / FULL_BAR_DAYS) * 100))),
  }
}

function count(n: number): string {
  return n.toLocaleString('pt-BR', { maximumFractionDigits: 1 })
}

/**
 * Quantos vídeos publicáveis a geração rende por dia, num número só. No
 * cortes, cada vídeo do dia é um episódio que vira vários cortes, e é isso
 * que os canais consomem: o backend já manda ``production_per_day`` como
 * gerações × cortes por episódio (o medido, com histórico; senão o teto).
 * Acima de 10, sem casa decimal: "13 vídeos/dia", não "13,4".
 */
export function productionText(pool: Pool): string {
  // O backend antigo não manda ``production_per_day`` (o front sai antes do pull).
  const perDay = (pool.production_per_day as number | undefined) ?? pool.videos_per_day
  const n = perDay >= 10 ? Math.round(perDay) : perDay
  return `${count(n)} ${n === 1 ? 'vídeo' : 'vídeos'}/dia`
}

/** O aviso do último pedido sem vídeo, se for das últimas 24 h. */
export function stockoutAlert(pool: Pool, now: Date): string | null {
  const last = (pool.stockouts ?? [])[0]
  if (!last) return null
  const at = new Date(last.occurred_at)
  if (Number.isNaN(at.getTime()) || now.getTime() - at.getTime() > 24 * 3_600_000) return null
  const who = pool.members.find((m) => m.slug === last.channel_slug)?.name ?? last.channel_slug
  const when = `${dayLabel(at, now)} ${String(at.getHours()).padStart(2, '0')}:${String(at.getMinutes()).padStart(2, '0')}`
  return last.reason === 'empty'
    ? `${who} pediu vídeo ${when} e o pool estava vazio.`
    : `${who} pediu vídeo ${when}: o pool tinha ${String(last.available)}, mas nenhum serviu ao canal.`
}

export function buildPoolCards(
  pools: readonly Pool[],
  factoryLabels: ReadonlyMap<string, string>,
  now: Date,
): PoolCardModel[] {
  return pools.map((pool) => {
    const stock = stockOf(pool)
    return {
      slug: pool.slug,
      name: pool.name,
      language: languageLabel(pool.language),
      factory: pool.factory,
      factoryLabel: factoryLabels.get(pool.factory) ?? pool.factory,
      accent: factoryAccent(pool.factory),
      schedule: pool.generation_time ? `todo dia às ${pool.generation_time}` : '',
      videosPerDay: pool.videos_per_day,
      productionText: productionText(pool),
      consumptionPerDay: pool.consumption_per_day,
      available: pool.available,
      daysOfStock: pool.days_of_stock ?? null,
      stock: stock.level,
      stockText: stock.text,
      stockPercent: stock.percent,
      members: pool.members.map((m) => ({
        slug: m.slug,
        name: m.name,
        initials: initials(m.name),
        avatarUrl: m.avatar_url ?? null,
      })),
      alert: stockoutAlert(pool, now),
      enabled: pool.enabled,
    }
  })
}

/** Busca por nome, slug ou factory, como na grade de canais. */
export function visiblePools(cards: readonly PoolCardModel[], query: string): PoolCardModel[] {
  const q = query
    .trim()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLocaleLowerCase('pt-BR')
  const sorted = [...cards].sort((a, b) => a.name.localeCompare(b.name, 'pt-BR'))
  if (!q) return sorted
  return sorted.filter((c) =>
    [c.name, c.slug, c.factoryLabel].some((t) =>
      t
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
        .toLocaleLowerCase('pt-BR')
        .includes(q),
    ),
  )
}
